/* eslint-disable max-lines, max-lines-per-function */
import initGangsheetModal from "components/supplier_admin/printer_queue/gangsheet_modal/gangsheet_modal";
import initNoteModal from "components/supplier_admin/printer_queue/note_modal/note_modal";
import initQueueCardActions from "components/supplier_admin/printer_queue/queue_card/queue_card";
import {
  clearPrinterQueueToast,
  initPrinterQueueToast,
  showPrinterQueueToast
} from "components/supplier_admin/printer_queue/toast/toast";

import applyPayload from "components/supplier_admin/printer_queue/printer_queue_dom";
import {
  createNote,
  requestQueue,
  requestSignal,
  undoSignal
} from "components/supplier_admin/printer_queue/printer_queue_api";
import {
  startPolling,
  pausePollingForInFlight,
  resumePollingForInFlight,
  handleVisibilityChange
} from "components/supplier_admin/shared/queue_polling";

const SELECTORS = {
  container: ".js-data-printer-queue",
  list: ".js-printer-queue-list",
  notice: ".js-printer-queue-notice",
  toast: ".js-printer-queue-toast",
  futureCount: ".js-printer-queue-future-count",
  summaryTotal: ".js-printer-queue-summary-total",
  summaryPrint: ".js-printer-queue-summary-print",
  summaryTransfer: ".js-printer-queue-summary-transfer",
  pagination: ".js-queue-pagination",
  emptyState: ".js-printer-queue-empty-state",
  card: ".js-printer-queue-card",
  actionButton: ".js-printer-queue-action"
};
const CLASSES = {
  loading: "printer-queue-card__action--loading"
};
const NOTE_SAVE_ERROR_MESSAGE = "Failed to save the note. Please try again.";
const FINISH_ERROR_MESSAGE = "Failed to finish the print job. Please try again.";
const SIGNAL_ERROR_MESSAGE =
  "Failed to update the print status. Please refresh the page and try again.";

const getCardButtons = $button => {
  if (!$button) return [];
  const $card = $button.closest(SELECTORS.card);
  return $card ? Array.from($card.querySelectorAll(SELECTORS.actionButton)) : [];
};

const setButtonsDisabled = ($buttons, disabled) => {
  for (const $actionButton of $buttons) {
    $actionButton.disabled = disabled;
    if (disabled) {
      $actionButton.setAttribute("aria-disabled", "true");
    } else {
      $actionButton.removeAttribute("aria-disabled");
    }
  }
};

const setButtonLoading = ({ $button, $cardButtons }) => {
  if (!$button) return;
  setButtonsDisabled($cardButtons, true);
  const labelText = $button.textContent.replace(/\s+/g, " ").trim();
  const priorAriaLabel = $button.getAttribute("aria-label");
  $button.dataset.priorAriaLabel = priorAriaLabel === null ? "" : priorAriaLabel;
  $button.setAttribute("aria-label", labelText ? `${labelText} (processing)` : "Processing");
  $button.setAttribute("aria-busy", "true");
  $button.classList.add(CLASSES.loading);
};

const clearButtonLoading = ({ $button, $cardButtons }) => {
  if (!$button) return;
  setButtonsDisabled($cardButtons, false);
  const { priorAriaLabel } = $button.dataset;
  if (priorAriaLabel === "") {
    $button.removeAttribute("aria-label");
  } else if (priorAriaLabel) {
    $button.setAttribute("aria-label", priorAriaLabel);
  }
  delete $button.dataset.priorAriaLabel;
  $button.removeAttribute("aria-busy");
  $button.classList.remove(CLASSES.loading);
};

const buildQueueActionRequest = ({ action, shipmentId, signalType, signalId }) =>
  action === "undo"
    ? undoSignal({ shipmentId, signalId })
    : requestSignal({ shipmentId, signalType });

const handleFinishNoteSubmit = async ({
  shipmentId,
  signalType,
  note,
  noteAlreadySaved,
  button: $button,
  fetchQueueFn,
  inFlightActionsRef,
  pollTimerIdRef,
  $toastEl
}) => {
  if (!shipmentId || !signalType) {
    return { shouldClose: false, errorMessage: FINISH_ERROR_MESSAGE, noteSaved: false };
  }

  const $cardButtons = getCardButtons($button);
  setButtonLoading({ $button, $cardButtons });
  pausePollingForInFlight({ inFlightActionsRef, pollTimerIdRef });

  let noteSaved = noteAlreadySaved;
  let errorMessage = null;

  try {
    if (note && !noteAlreadySaved) {
      await createNote({ shipmentId, body: note });
      noteSaved = true;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Printer Queue] Finish note save failed.", error);
    errorMessage = NOTE_SAVE_ERROR_MESSAGE;
  }

  if (!errorMessage) {
    try {
      await requestSignal({ shipmentId, signalType });
      clearPrinterQueueToast($toastEl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[Printer Queue] Signal Status update failed.", error);
      showPrinterQueueToast($toastEl, SIGNAL_ERROR_MESSAGE);
      errorMessage = FINISH_ERROR_MESSAGE;
    }
  }

  try {
    await fetchQueueFn();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Printer Queue] Fetch queue failed after finish.", error);
  } finally {
    resumePollingForInFlight({ inFlightActionsRef, pollTimerIdRef, fetchQueueFn });
    clearButtonLoading({ $button, $cardButtons });
  }

  return { shouldClose: !errorMessage, errorMessage, noteSaved };
};

const handleQueueAction = async ({
  detail,
  fetchQueueFn,
  inFlightActionsRef,
  pollTimerIdRef,
  $toastEl,
  noteModal
}) => {
  if (!detail) return;
  const { action, shipmentId, signalType, signalId, button: $button } = detail;
  if (!shipmentId) return;
  if ($button && $button.disabled) return;

  if (action === "finish" && noteModal) {
    noteModal.open({ shipmentId, signalType, orderId: detail.orderId, button: $button });
    return;
  }

  const $cardButtons = getCardButtons($button);
  setButtonLoading({ $button, $cardButtons });
  pausePollingForInFlight({ inFlightActionsRef, pollTimerIdRef });
  const request = buildQueueActionRequest({ action, shipmentId, signalType, signalId });

  try {
    await request;
    clearPrinterQueueToast($toastEl);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Printer Queue] Signal Status update failed.", error);
    showPrinterQueueToast(
      $toastEl,
      "Failed to update the print status. Please refresh the page and try again."
    );
  } finally {
    await fetchQueueFn();
    resumePollingForInFlight({ inFlightActionsRef, pollTimerIdRef, fetchQueueFn });
    clearButtonLoading({ $button, $cardButtons });
  }
};

const fetchQueue = ({
  state,
  $listEl,
  $container,
  $noticeEl,
  $futureCountEl,
  $summaryTotalEl,
  $summaryPrintEl,
  $summaryTransferEl,
  $paginationEl,
  $emptyStateEl,
  isFetchingRef
}) => {
  if (isFetchingRef.value) return Promise.resolve();
  isFetchingRef.value = true;

  return requestQueue(state)
    .then(payload => {
      if (!payload) return;
      applyPayload({
        payload,
        state,
        $container,
        $listEl,
        $noticeEl,
        $futureCountEl,
        $summaryTotalEl,
        $summaryPrintEl,
        $summaryTransferEl,
        $emptyStateEl,
        $paginationEl
      });
    })
    .catch(() => {})
    .finally(() => {
      isFetchingRef.value = false;
    });
};

const initPrinterQueue = $container => {
  const $listEl = $container.querySelector(SELECTORS.list);
  if (!$listEl) return;

  const $noticeEl = $container.querySelector(SELECTORS.notice);
  const $toastEl = $container.querySelector(SELECTORS.toast);
  const $futureCountEl = $container.querySelector(SELECTORS.futureCount);
  const $summaryTotalEl = $container.querySelector(SELECTORS.summaryTotal);
  const $summaryPrintEl = $container.querySelector(SELECTORS.summaryPrint);
  const $summaryTransferEl = $container.querySelector(SELECTORS.summaryTransfer);
  const $paginationEl = $container.querySelector(SELECTORS.pagination);
  const $emptyStateEl = $container.querySelector(SELECTORS.emptyState);

  const state = {
    date: $container.dataset.printerQueueDate || "",
    page: $container.dataset.printerQueuePage || "1",
    perPage: $container.dataset.printerQueuePerPage || "10",
    lastUpdated: $container.dataset.printerQueueLastUpdated || "",
    status: $container.dataset.printerQueueStatus || "active"
  };

  const canCreateNotes = $container.dataset.printerQueueCanCreateNotes === "true";
  const pollTimerIdRef = { value: null };
  const pauseTimerIdRef = { value: null };
  const inFlightActionsRef = { value: 0 };
  const isFetchingRef = { value: false };

  const fetchQueueFn = () =>
    fetchQueue({
      state,
      $listEl,
      $container,
      $noticeEl,
      $futureCountEl,
      $summaryTotalEl,
      $summaryPrintEl,
      $summaryTransferEl,
      $paginationEl,
      $emptyStateEl,
      isFetchingRef
    });

  initGangsheetModal($container);
  const noteModal = canCreateNotes
    ? initNoteModal({
        $container,
        onSubmit: ({ shipmentId, signalType, note, noteAlreadySaved, button: $button }) =>
          handleFinishNoteSubmit({
            shipmentId,
            signalType,
            note,
            noteAlreadySaved,
            button: $button,
            fetchQueueFn,
            inFlightActionsRef,
            pollTimerIdRef,
            $toastEl
          })
      })
    : null;
  initPrinterQueueToast($container);
  initQueueCardActions($container);
  $container.addEventListener("printer-queue:signal-change-action", event =>
    handleQueueAction({
      detail: event.detail,
      fetchQueueFn,
      inFlightActionsRef,
      pollTimerIdRef,
      $toastEl,
      noteModal
    })
  );

  document.addEventListener("visibilitychange", () =>
    handleVisibilityChange({ pauseTimerIdRef, pollTimerIdRef, fetchQueueFn, inFlightActionsRef })
  );
  startPolling({ pollTimerIdRef, fetchQueueFn });
};

document.addEventListener("DOMContentLoaded", () => {
  const $container = document.querySelector(SELECTORS.container);
  if ($container) initPrinterQueue($container);
});
